import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { AuthUtils } from 'app/configuration/auth/auth.utils';
import { UserService } from 'app/configuration/user/user.service';
import { manageStorageService } from '@core/services/manage-storage/manage-storage.service';

/**
 * Servicio de autenticación para la gestión de sesiones de usuario.
 * 
 * Este servicio proporciona métodos para autenticación, validación de tokens
 * y gestión de diferentes tipos de sesiones (acceso normal, primer acceso, cambio de rol).
 * Almacena los tokens de sesión en el almacenamiento local del navegador.
 * 
 * @class AuthService
 */
@Injectable()
export class AuthService {
    /** Indica si el usuario está autenticado en la sesión actual. */
    private _authenticated: boolean = false;
    /** Indica si el usuario está autenticado en el flujo de primer acceso. */
    private _authenticatedFirstAccess: boolean = false;
    /** Indica si el usuario está autenticado en el flujo de cambio de rol. */
    private _authenticatedChangeRole: boolean = false;

    /**
     * Constructor del servicio de autenticación.
     * 
     * Inicializa las dependencias requeridas para la gestión de autenticación,
     * incluyendo el cliente HTTP, servicio de usuario y servicio de almacenamiento.
     * 
     * @param {HttpClient} _httpClient - Cliente HTTP para realizar peticiones al servidor.
     * @param {UserService} _userService - Servicio de usuario para gestionar datos del usuario.
     * @param {manageStorageService} _mStorage - Servicio de almacenamiento para persistencia de datos.
     */
    constructor(
        private _httpClient: HttpClient,
        private _userService: UserService,
        private _mStorage: manageStorageService
    ) {
    }
    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Obtiene o establece el token de acceso del usuario en el almacenamiento.
     * 
     * @type {string}
     * @returns {string} Token de acceso almacenado o cadena vacía si no existe.
     */
    set accessToken(token: string) { }
    get accessToken(): string { return this._mStorage.getPropertie("s_accsTkn") ?? ""; }

    /**
     * Obtiene o establece el token de primer acceso del usuario en el almacenamiento.
     * 
     * @type {string}
     * @returns {string} Token de primer acceso almacenado o cadena vacía si no existe.
     */
    set accessFirstToken(token: string) { }
    get accessFirstToken(): string { return this._mStorage.getPropertie("s_fAccsTkn") ?? ""; }

    /**
     * Obtiene o establece el token de cambio de rol del usuario en el almacenamiento.
     * 
     * @type {string}
     * @returns {string} Token de cambio de rol almacenado o cadena vacía si no existe.
     */
    set accessChangeRoleToken(token: string) { }
    get accessChangeRoleToken(): string { return this._mStorage.getPropertie("s_cRlsTkn") ?? ""; }
    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Solicita el restablecimiento de contraseña para un usuario.
     * 
     * Envía una petición POST al servidor con el identificador del usuario
     * para iniciar el proceso de recuperación de contraseña.
     * 
     * @param {string} usr - Identificador o correo electrónico del usuario.
     * @returns {Observable<any>} Observable con la respuesta del servidor.
     * 
     * @example
     * this.authService.forgotPassword('usuario@ejemplo.com').subscribe(
     *   (response) => console.log('Correo enviado', response),
     *   (error) => console.error('Error', error)
     * );
     */
    forgotPassword(usr: string): Observable<any> {
        return this._httpClient.post('api/auth/forgot-password', usr);
    }

    /**
     * Inicia sesión con las credenciales proporcionadas.
     * 
     * Autentica un usuario enviando sus credenciales al servidor. Si la autenticación
     * es exitosa, almacena el token de acceso, establece el estado autenticado y
     * guarda los datos del usuario en el servicio de usuario.
     * 
     * @param {Object} credentials - Objeto con las credenciales del usuario.
     * @param {string} credentials.email - Correo electrónico del usuario.
     * @param {string} credentials.password - Contraseña del usuario.
     * @returns {Observable<any>} Observable con la respuesta del servidor incluyendo el usuario y token.
     * @throws {string} Lanza un error si el usuario ya está autenticado.
     * 
     * @example
     * const credentials = { email: 'usuario@ejemplo.com', password: 'contraseña123' };
     * this.authService.signIn(credentials).subscribe(
     *   (response) => console.log('Sesión iniciada', response),
     *   (error) => console.error('Error de autenticación', error)
     * ); @deprecated
     */
    // signIn(credentials: { email: string; password: string }): Observable<any> {
    //     // Throw error, if the user is already logged in
    //     if (this._authenticated) {
    //         return throwError('User is already logged in.');
    //     }
    //     return this._httpClient.post('api/auth/sign-in', credentials).pipe(
    //         switchMap((response: any) => {
    //             // Store the access token in the local storage
    //             // this.accessToken = response.accessToken;
    //             this.accessToken = this._mStorage.getPropertie("s_accsTkn") ?? "";
    //             // Set the authenticated flag to true
    //             this._authenticated = true;
    //             // Store the user on the user service
    //             this._userService.user = response.user;
    //             // Return a new observable with the response
    //             return of(response);
    //         })
    //     );
    // }

    /**
     * Inicia sesión utilizando un token de acceso almacenado.
     * 
     * Recupera el token de acceso del almacenamiento de sesión y establece
     * el estado de autenticación como verdadero.
     * 
     * @returns {Observable<boolean>} Observable que devuelve true si la sesión se inicia correctamente.
     * 
     * @example
     * this.authService.signInUsingToken().subscribe(
     *   (success) => console.log('Sesión iniciada con token:', success)
     * );
     */
    signInUsingToken(): Observable<any> {
        // Store the access token in the local storage
        this.accessToken = sessionStorage.getItem("s_accsTkn") ?? ""; // response.accessToken;
        // Set the authenticated flag to true
        this._authenticated = true;
        // Return true
        return of(true);
    }

    /**
     * Inicia sesión con token de primer acceso.
     * 
     * Recupera el token de primer acceso del almacenamiento de sesión
     * y establece el estado de autenticación de primer acceso como verdadero.
     * Este flujo se utiliza típicamente para usuarios nuevos o en procesos especiales.
     * 
     * @returns {Observable<boolean>} Observable que devuelve true si la sesión se inicia correctamente.
     * 
     * @example
     * this.authService.signInFirstAcccess().subscribe(
     *   (success) => console.log('Primer acceso iniciado:', success)
     * );
     */
    signInFirstAcccess(): Observable<any> {
        // Store the access token in the local storage
        this.accessToken = sessionStorage.getItem("s_fAccsTkn") ?? ""; // response.accessToken;
        // Set the authenticated flag to true
        this._authenticatedFirstAccess = true;
        // Return true
        return of(true);
    }

    /**
     * Inicia sesión con token de cambio de rol.
     * 
     * Recupera el token de cambio de rol del almacenamiento de sesión
     * y establece el estado de autenticación de cambio de rol como verdadero.
     * Este flujo se utiliza cuando un usuario cambia su rol dentro del sistema.
     * 
     * @returns {Observable<boolean>} Observable que devuelve true si la sesión se inicia correctamente.
     * 
     * @example
     * this.authService.signInChangeRole().subscribe(
     *   (success) => console.log('Cambio de rol iniciado:', success)
     * );
     */
    signInChangeRole(): Observable<any> {
        // Store the access token in the local storage
        this.accessToken = sessionStorage.getItem("s_cRlsTkn") ?? ""; // response.accessToken;
        // Set the authenticated flag to true
        this._authenticatedChangeRole = true;
        // Return true
        return of(true);
    }

    /**
     * Cierra la sesión del usuario.
     * 
     * Elimina todos los tokens almacenados (acceso normal, primer acceso y cambio de rol),
     * restablece todos los estados de autenticación a false y limpia la sesión del usuario.
     * 
     * @returns {Observable<boolean>} Observable que devuelve true indicando que la sesión se ha cerrado.
     * 
     * @example
     * this.authService.signOut().subscribe(
     *   (success) => console.log('Sesión cerrada correctamente')
     * );
     */
    signOut(): Observable<any> {
        // Remove the access token from the local storage
        this._mStorage.clearStorage("s_accsTkn")
        this._mStorage.clearStorage("s_fAccsTkn")
        this._mStorage.clearStorage("s_cRlsTkn")
        // Set the authenticated flag to false
        this._authenticated = false;
        this._authenticatedFirstAccess = false;
        this._authenticatedChangeRole = false;
        // Return the observable
        return of(true);
    }

    /**
     * Verifica el estado de autenticación del usuario.
     * 
     * Realiza una validación en cascada:
     * 1. Comprueba si el usuario ya está autenticado en memoria.
     * 2. Verifica la existencia del token de acceso en almacenamiento.
     * 3. Valida que el token no haya expirado.
     * 4. Si todo es válido, inicia sesión con el token existente.
     * 
     * @returns {Observable<boolean>} Observable que devuelve true si el usuario está autenticado, false en caso contrario.
     * 
     * @example
     * this.authService.check().subscribe(
     *   (isAuthenticated) => {
     *     if (isAuthenticated) {
     *       console.log('Usuario autenticado');
     *     } else {
     *       console.log('Usuario no autenticado');
     *     }
     *   }
     * );
     */
    check(): Observable<boolean> {
        // Check if the user is logged in
        if (this._authenticated) { return of(true); }
        // Check the access token availability
        if (!this.accessToken) { return of(false); }
        // Check the access token expire date
        if (AuthUtils.isTokenExpired(this.accessToken)) { return of(false); }
        // If the access token exists and it didn't expire, sign in using it
        return this.signInUsingToken();
    }

    /**
     * Verifica el estado de autenticación para el flujo de primer acceso.
     * 
     * Realiza una validación similar a {@link check()} pero específicamente para el
     * flujo de primer acceso. Comprueba:
     * 1. Si el usuario está autenticado en el flujo de primer acceso.
     * 2. La existencia del token de primer acceso.
     * 3. Que el token no haya expirado.
     * 
     * @returns {Observable<boolean>} Observable que devuelve true si el usuario está autenticado en primer acceso, false en caso contrario.
     * 
     * @example
     * this.authService.checkFirstAccess().subscribe(
     *   (isFirstAccessAuth) => console.log('Primer acceso válido:', isFirstAccessAuth)
     * );
     */
    checkFirstAccess(): Observable<boolean> {
        // Check if the user is logged in
        if (this._authenticatedFirstAccess) { return of(true); }
        // Check the access token availability
        if (!this.accessFirstToken) { return of(false); }
        // Check the access token expire date
        if (AuthUtils.isTokenExpired(this.accessFirstToken)) { return of(false); }
        // If the access token exists and it didn't expire, sign in using it
        return this.signInFirstAcccess();
    }

    /**
     * Verifica el estado de autenticación para el flujo de cambio de rol.
     * 
     * Realiza una validación similar a {@link check()} pero específicamente para el
     * flujo de cambio de rol. Comprueba:
     * 1. Si el usuario está autenticado en el flujo de cambio de rol.
     * 2. La existencia del token de cambio de rol.
     * 3. Que el token no haya expirado.
     * 
     * @returns {Observable<boolean>} Observable que devuelve true si el usuario está autenticado en cambio de rol, false en caso contrario.
     * 
     * @example
     * this.authService.checkChangeRole().subscribe(
     *   (isChangeRoleAuth) => console.log('Cambio de rol válido:', isChangeRoleAuth)
     * );
     */
    checkChangeRole(): Observable<boolean> {
        // Check if the user is logged in
        if (this._authenticatedChangeRole) { return of(true); }
        // Check the access token availability
        if (!this.accessChangeRoleToken) { return of(false); }
        // Check the access token expire date
        if (AuthUtils.isTokenExpired(this.accessChangeRoleToken)) { return of(false); }
        // If the access token exists and it didn't expire, sign in using it
        return this.signInChangeRole();
    }
}
